import { useCallback, useEffect, useRef, useState } from 'react'

import {
  Card,
  Flex,
  Form,
  Header,
  PrimaryButton,
  SecondaryButton,
  Space,
  StatusBadge,
  TertiaryButton,
  WithFormControlSelect,
} from '@procurify/ui'
import {
  type FormikHelpers,
  type FormikProps,
  FormikProvider,
  setNestedObjectValues,
  useFormik,
} from 'formik'
import * as Yup from 'yup'

import { type RowCellMenuListItemProps } from '@webapp/components/DataTable'
import { toast } from '@webapp/components/Notification'
import { CostSummary } from '@webapp/core/components/CostSummary'
import { Price } from '@webapp/core/components/Price'
import { type Currency } from '@webapp/core/entities/external/Currency'
import { useProcurifyIntl } from '@webapp/hooks'
import { computeExpenseReportTotalsUseCase } from '@webapp/modules/expense/business-logic/use-cases/ComputeExpenseReportTotals'
import {
  AttachableTravelTicketSelect,
  type AttachableTravelTicketSelectValue,
} from '@webapp/modules/expense/components/AttachableTravelTicketSelect'
import { ExpenseItemTable } from '@webapp/modules/expense/components/ExpenseItemTable'
import {
  ExpenseLineItemDrawer,
  type ExpenseItemFormSchema,
} from '@webapp/modules/expense/components/ExpenseLineItemDrawer'
import {
  RequestablePrimaryCategorySelect,
  type RequestablePrimaryCategorySelectValue,
} from '@webapp/modules/expense/components/RequestablePrimaryCategorySelect'
import {
  RequestableSecondaryCategorySelect,
  type RequestableSecondaryCategorySelectValue,
} from '@webapp/modules/expense/components/RequestableSecondaryCategorySelect'
import { type ExpenseFormReimbursementCustomization } from '@webapp/modules/expense/entities/enums'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import { type RequestableApproverOption } from '@webapp/modules/expense/entities/RequestableApproverOption'
import { mapExpenseItemFormSchemaToExpenseItem } from './mapExpenseItemFormSchemaToExpenseItem'
import messages from './messages'

export interface ExpenseReportFormSchema {
  id: Nullable<number>
  title: string
  department: RequestableSecondaryCategorySelectValue | null
  location: RequestablePrimaryCategorySelectValue | null
  travel: AttachableTravelTicketSelectValue | null
  notes: string
  createdDate: string
  nextApproverId: Nullable<number>
  items: ExpenseItemFormSchema[]
}

export interface ICreateExpenseReportFormProps {
  initialValues: ExpenseReportFormSchema
  userId: number
  isDraft: boolean
  reimbursementSetting: ExpenseFormReimbursementCustomization
  approverOptions: RequestableApproverOption[]
  isApproverLoading: boolean
  isLoading: boolean
  formRef?: React.MutableRefObject<FormikProps<ExpenseReportFormSchema> | null>
  baseCurrency: Nullable<Currency>
  onSubmit: (
    values: ExpenseReportFormSchema,
    actions: FormikHelpers<ExpenseReportFormSchema>
  ) => void
  onSaveDraft: (values: ExpenseReportFormSchema) => void
  onReset: () => void
  onDepartmentChange: (departmentId: number) => void
  onAmountChange: (amount: number) => void
}

export const CreateExpenseReportForm = ({
  initialValues,
  userId,
  isDraft,
  reimbursementSetting,
  approverOptions,
  isApproverLoading,
  isLoading,
  formRef,
  baseCurrency,
  onDepartmentChange,
  onAmountChange,
  onSubmit,
  onSaveDraft,
  onReset,
}: ICreateExpenseReportFormProps) => {
  const intl = useProcurifyIntl()
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)
  const [editingItemIndex, setEditingItemIndex] =
    useState<Nullable<number>>(null)

  const validationSchema = Yup.object({
    title: Yup.string()
      .required(intl.formatMessage(messages.titleRequired))
      .trim(),
    location: Yup.object()
      .nullable()
      .required(intl.formatMessage(messages.locationRequired)),
    department: Yup.object()
      .nullable()
      .required(intl.formatMessage(messages.departmentRequired)),
    travel: Yup.mixed().nullable(),
    notes: Yup.string()
      .nullable()
      .max(2000, intl.formatMessage(messages.purposeMaxLength)),
    nextApproverId: Yup.number()
      .nullable()
      .required(intl.formatMessage(messages.nextApproverRequired)),
    items: Yup.array().min(1, intl.formatMessage(messages.itemsMinRequired)),
  })

  const formik = useFormik<ExpenseReportFormSchema>({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: false,
  })

  useEffect(() => {
    if (formRef) {
      formRef.current = formik
    }
  }, [formRef, formik])

  const handleCreateItemSuccess = useCallback(
    (item: ExpenseItemFormSchema) => {
      formik.setFieldValue('items', [...formik.values.items, item])
      setCreateDrawerOpen(false)
    },
    [formik]
  )

  const handleEditItemSuccess = useCallback(
    (values: ExpenseItemFormSchema) => {
      if (editingItemIndex === null) return
      const updatedItems = formik.values.items.map((item, idx) =>
        idx === editingItemIndex ? values : item
      )
      formik.setFieldValue('items', updatedItems)
      setEditingItemIndex(null)
    },
    [editingItemIndex, formik]
  )

  const { department, items } = formik.values

  const isDepartmentInitialized = useRef(true)
  useEffect(() => {
    if (isDepartmentInitialized.current) {
      isDepartmentInitialized.current = false
      return
    }
    if (department != null) {
      onDepartmentChange(department.id)
    }
  }, [department, onDepartmentChange])

  const isLocationInitialized = useRef(true)
  useEffect(() => {
    if (isLocationInitialized.current) {
      isLocationInitialized.current = false
      return
    }
    formik.setFieldValue('department', null)
  }, [formik.values.location?.id])

  const showNoItemsToast = () => {
    toast.info({
      title: intl.formatMessage(messages.emptyItemsTitle),
      children: intl.formatMessage(messages.emptyItemsMessage),
    })
  }

  const handleSaveDraftClick = useCallback(async () => {
    if (formik.values.items.length === 0) {
      showNoItemsToast()
      return
    }
    const errors = await formik.validateForm()
    if (Object.keys(errors).length > 0) {
      formik.setTouched(setNestedObjectValues(errors, true))
      return
    }
    onSaveDraft(formik.values)
  }, [formik, intl, onSaveDraft])

  const { totalAmount, totalTax, subtotal, reimbursableTotal } =
    computeExpenseReportTotalsUseCase.execute({ items })

  useEffect(() => {
    onAmountChange(totalAmount)
  }, [totalAmount, onAmountChange])

  const getMenuListItems = (
    item: ExpenseItem,
    rowIndex: number
  ): RowCellMenuListItemProps[] => [
    {
      icon: 'edit',
      text: intl.formatMessage(messages.editMenuLabel),
      onClick: () => setEditingItemIndex(rowIndex),
    },
    {
      icon: 'duplicate',
      text: intl.formatMessage(messages.duplicateMenuLabel),
      onClick: () => {
        const itemToDuplicate = formik.values.items[rowIndex]
        if (itemToDuplicate) {
          const duplicated: ExpenseItemFormSchema = {
            ...itemToDuplicate,
            id: null,
          }
          const newItems = [...formik.values.items]
          newItems.splice(rowIndex + 1, 0, duplicated)
          formik.setFieldValue('items', newItems)
        }
      },
    },
    {
      icon: 'delete',
      text: intl.formatMessage(messages.removeMenuLabel),
      onClick: () => {
        const newItems = formik.values.items.filter(
          (_, idx) => idx !== rowIndex
        )
        formik.setFieldValue('items', newItems)
      },
    },
  ]

  const approverSelectOptions = approverOptions.map((option) => ({
    label: option.displayLabel,
    value: String(option.id),
  }))

  const selectedApproverOption =
    formik.values.nextApproverId != null
      ? (approverSelectOptions.find(
          (opt) => opt.value === String(formik.values.nextApproverId)
        ) ?? null)
      : null

  return (
    <>
      <FormikProvider value={formik}>
        <Space>
          <Form.Content noValidate autoComplete='off'>
            <Space size='large'>
              <Card>
                <Card.Content>
                  <Space>
                    <Space direction='horizontal' align='center'>
                      <Header as='h2'>
                        {intl.formatMessage(messages.formHeading)}
                      </Header>
                      {isDraft && (
                        <StatusBadge
                          type='inactive'
                          text={intl.formatMessage(messages.draftBadge)}
                        />
                      )}
                    </Space>
                    <Flex.Row>
                      <Flex.Item width={1 / 2}>
                        <Form.TextField
                          id='title'
                          name='title'
                          label={intl.formatMessage(messages.titleLabel)}
                          required
                          placeholder={intl.formatMessage(
                            messages.titlePlaceholder
                          )}
                          inputProps={{ maxLength: 100 }}
                        />
                      </Flex.Item>
                      <Flex.Item width={1 / 2}>
                        <WithFormControlSelect
                          id='travel'
                          name='travel'
                          label={intl.formatMessage(messages.travelTicketLabel)}
                        >
                          <AttachableTravelTicketSelect
                            userId={userId}
                            onChange={(option) =>
                              formik.setFieldValue('travel', option)
                            }
                            isClearable
                          />
                        </WithFormControlSelect>
                      </Flex.Item>
                    </Flex.Row>
                    <Flex.Row>
                      <Flex.Item width={1 / 2}>
                        <WithFormControlSelect
                          name='location'
                          id='location'
                          labelFor='location-select-input'
                          label={intl.formatMessage(messages.locationLabel)}
                          required
                        >
                          <RequestablePrimaryCategorySelect
                            id='location'
                            inputId='location-select-input'
                          />
                        </WithFormControlSelect>
                      </Flex.Item>
                      <Flex.Item width={1 / 2}>
                        <WithFormControlSelect
                          name='department'
                          id='department'
                          labelFor='department-select-input'
                          label={intl.formatMessage(messages.departmentLabel)}
                          required
                        >
                          <RequestableSecondaryCategorySelect
                            id='department'
                            inputId='department-select-input'
                            locationId={formik.values.location?.id ?? null}
                            isDisabled={formik.values.location == null}
                          />
                        </WithFormControlSelect>
                      </Flex.Item>
                    </Flex.Row>
                    <Form.TextField
                      id='notes'
                      name='notes'
                      label={intl.formatMessage(messages.purposeLabel)}
                      multiline
                      minRows={3}
                      placeholder={intl.formatMessage(
                        messages.purposePlaceholder
                      )}
                      inputProps={{ maxLength: 2000 }}
                    />
                  </Space>
                </Card.Content>
              </Card>
              <Card>
                <Card.Content>
                  <Space size='large'>
                    <Space
                      direction='horizontal'
                      justify='space-between'
                      align='center'
                    >
                      <Header as='h4'>
                        {intl.formatMessage(messages.expenseItemsHeading)}
                      </Header>
                      <SecondaryButton
                        type='button'
                        icon='add'
                        onClick={() => setCreateDrawerOpen(true)}
                      >
                        {intl.formatMessage(messages.addExpenseItemButton)}
                      </SecondaryButton>
                    </Space>
                    <ExpenseItemTable
                      items={formik.values.items.map(
                        mapExpenseItemFormSchemaToExpenseItem
                      )}
                      menuListItems={getMenuListItems}
                      onAttachmentClick={(_item, index) =>
                        setEditingItemIndex(index)
                      }
                    />
                    <Space
                      direction='horizontal'
                      justify='space-between'
                      align='start'
                    >
                      <Space.Item width={1 / 2}>
                        <Space>
                          <Form.Select
                            id='nextApproverId'
                            name='nextApproverId'
                            label={intl.formatMessage(
                              messages.nextApproverLabel
                            )}
                            required
                            options={approverSelectOptions}
                            value={selectedApproverOption}
                            onChange={(option) => {
                              const selected = option as {
                                value: string
                              } | null
                              formik.setFieldValue(
                                'nextApproverId',
                                selected ? parseInt(selected.value, 10) : null
                              )
                            }}
                            placeholder={intl.formatMessage(
                              messages.nextApproverPlaceholder
                            )}
                            disabled={isApproverLoading}
                          />
                          {isApproverLoading && (
                            <StatusBadge
                              type='inProgress'
                              text={intl.formatMessage(
                                messages.resolvingApproverLabel
                              )}
                            />
                          )}
                        </Space>
                      </Space.Item>
                      <Space.Item width={1 / 2}>
                        <Space direction='vertical' align='end'>
                          <CostSummary>
                            <CostSummary.Cost>
                              <th id='expense-form-cost-summary-subtotal'>
                                {intl.formatMessage(messages.subtotalLabel)}
                              </th>
                              <td>
                                <Price
                                  price={subtotal}
                                  fontSize={14}
                                  aria-labelledby='expense-form-cost-summary-subtotal'
                                />
                              </td>
                            </CostSummary.Cost>
                            <CostSummary.Cost>
                              <th id='expense-form-cost-summary-tax'>
                                {intl.formatMessage(messages.taxLabel)}
                              </th>
                              <td>
                                <Price
                                  price={totalTax}
                                  fontSize={14}
                                  aria-labelledby='expense-form-cost-summary-tax'
                                />
                              </td>
                            </CostSummary.Cost>
                            <CostSummary.Cost>
                              <th id='expense-form-cost-summary-reimbursable'>
                                {intl.formatMessage(messages.reimbursableLabel)}
                              </th>
                              <td>
                                <Price
                                  price={reimbursableTotal}
                                  fontSize={14}
                                  aria-labelledby='expense-form-cost-summary-reimbursable'
                                />
                              </td>
                            </CostSummary.Cost>
                            <CostSummary.Total>
                              <th id='expense-form-cost-summary-total'>
                                {intl.formatMessage(messages.totalLabel)}
                              </th>
                              <td>
                                <Price
                                  price={totalAmount}
                                  fontSize={18}
                                  aria-labelledby='expense-form-cost-summary-total'
                                />
                              </td>
                            </CostSummary.Total>
                          </CostSummary>
                        </Space>
                      </Space.Item>
                    </Space>
                  </Space>
                </Card.Content>
              </Card>
            </Space>
          </Form.Content>

          <Space direction='horizontal' justify='end' align='center'>
            <TertiaryButton
              type='button'
              onClick={onReset}
              disabled={isLoading}
            >
              {intl.formatMessage(messages.resetButton)}
            </TertiaryButton>
            <SecondaryButton
              type='button'
              onClick={handleSaveDraftClick}
              disabled={isLoading}
            >
              {intl.formatMessage(messages.saveDraftButton)}
            </SecondaryButton>
            <PrimaryButton
              type='button'
              onClick={() => {
                if (formik.values.items.length === 0) {
                  showNoItemsToast()
                  return
                }
                formik.handleSubmit()
              }}
              disabled={isLoading || isApproverLoading}
            >
              {intl.formatMessage(messages.submitButton)}
            </PrimaryButton>
          </Space>
        </Space>
      </FormikProvider>

      {editingItemIndex !== null ? (
        <ExpenseLineItemDrawer
          drawerOpen
          onClose={() => setEditingItemIndex(null)}
          item={
            formik.values.items[editingItemIndex] != null
              ? mapExpenseItemFormSchemaToExpenseItem(
                  formik.values.items[editingItemIndex]
                )
              : undefined
          }
          onSubmit={handleEditItemSuccess}
          reimbursementSetting={reimbursementSetting}
          requesterId={userId}
          secondaryCategoryId={formik.values.department?.id}
          baseCurrency={baseCurrency}
        />
      ) : (
        <ExpenseLineItemDrawer
          drawerOpen={createDrawerOpen}
          onClose={() => setCreateDrawerOpen(false)}
          onSubmit={handleCreateItemSuccess}
          reimbursementSetting={reimbursementSetting}
          requesterId={userId}
          secondaryCategoryId={formik.values.department?.id}
          baseCurrency={baseCurrency}
        />
      )}
    </>
  )
}
